// Pharmascience — quiz-result ACTION ROW assembly (Q-7, iteration 7).
//
// Consumer of `.bnrmp-result__actions`, emitted by
// learndash/ld30/quiz/partials/show_quiz_result_box.php.
//
// ── WHAT THIS DOES, AND WHAT IT DELIBERATELY DOES NOT ───────────────────────
// The previews' action row mixes buttons we own with buttons LearnDash owns:
//
//   Back to Course            ours       (an anchor, emitted in PHP)
//   Continue to Module {n+1}  ours       (an anchor, emitted in PHP)
//   Retake Quiz               LEARNDASH  input.wpProQuiz_button_restartQuiz
//   Review answers            LEARNDASH  input.wpProQuiz_button_reShowQuestion
//
// 🔴 THE TWO LEARNDASH CONTROLS ARE MOVED, NEVER REBUILT. This module calls
// `appendChild` on the EXISTING nodes. It never creates a replacement, never
// copies a label, never attaches a click handler, and never triggers one. That
// is the whole point: doctrine T2 forbids re-emitting `.ld-quiz-actions`
// because those elements carry LearnDash's own behaviour — the restart handler
// is what keeps FAIL retakes unlimited (BRD §5.1), and `p.wpProQuiz_certificate`
// beside them carries the NONCE-SIGNED certificate link, which is left exactly
// where LearnDash put it and is never touched here.
//
// ── WHY A DOM MOVE IS SAFE HERE (measured, not assumed) ─────────────────────
// 1. LD binds these buttons DIRECTLY, not by delegation:
//        wpProQuiz_front.js:5262  $e.find('input[name="reShowQuestion"]').on('click', …)
//        wpProQuiz_front.js:5266  $e.find('input[name="restartQuiz"]').on('click', …)
//    A directly-attached listener belongs to the NODE, so it travels with the
//    node. (Had LD delegated from a container, moving the node out of that
//    container would have silently killed the button — which is exactly why
//    this was checked before choosing the technique.)
//
// 2. LD ALSO reaches for these by name later:
//        :2947  $e.find('input[name="restartQuiz"]').remove()
//        :4103  $e.find('input[name="restartQuiz"]').hide()
//    where `$e` is `.wpProQuiz_content`. The destination row renders INSIDE
//    `.wpProQuiz_content` — proven by the state-class rules in `_learndash.scss`
//    matching `.bnrmp-result__actions` as a DESCENDANT selector — so those calls
//    still find the buttons after the move and LD's own show/hide/remove logic
//    keeps working.
//
// ── FAILURE MODE ────────────────────────────────────────────────────────────
// If this module never runs, the buttons simply stay in LearnDash's own
// `.ld-quiz-actions` row, which is still styled and still works. The degraded
// state is "the design is not applied", never "the learner cannot retake the
// quiz". `.ld-quiz-actions:empty` is what collapses the old row, so it can only
// disappear once its children have genuinely left.
//
// 🔴 PRESENTATION ONLY. Nothing here scores, decides pass/fail, alters a label,
// or touches the certificate link.

/**
 * The result box is rendered by LearnDash CLIENT-SIDE, after the quiz is
 * submitted — the buttons do not exist at DOMContentLoaded. So the move is
 * driven by a MutationObserver on the quiz content rather than run once.
 *
 * @param {HTMLElement} row The `.bnrmp-result__actions` destination row.
 * @return {boolean} True once both candidate controls have been dealt with.
 */
function relocate( row ) {
	const group = row.querySelector( '.bnrmp-completion__actions-group' );

	if ( ! group ) {
		return false;
	}

	let moved = 0;

	// "Review answers" joins the right-hand GROUP, beside "Continue to Module N"
	// — the previews put those two together (quiz-result-success.html:214-222).
	const review = document.querySelector( '.ld-quiz-actions input.wpProQuiz_button_reShowQuestion' );

	if ( review ) {
		group.appendChild( review );
		moved += 1;
	}

	// "Retake Quiz" is the FAIL screen's forward action and sits alone on the
	// right (quiz-result-fail.html:241-247), so it joins the group too — the
	// group is the row's right-hand slot in both states.
	const restart = document.querySelector( '.ld-quiz-actions input.wpProQuiz_button_restartQuiz' );

	if ( restart ) {
		group.appendChild( restart );
		moved += 1;
	}

	return moved > 0;
}

/**
 * Reveal the "View your certificate" button when THIS pass completes the course.
 *
 * ── WHY THIS EXISTS (measured 2026-08-24) ───────────────────────────────────
 * The result box is rendered server-side at PAGE LOAD and revealed by LearnDash
 * after the scoring AJAX, with no reload. So on the submission that actually
 * completes the course, PHP's `learndash_course_completed()` was still `false`
 * when the markup was built — proven on a probe user left at 2-of-3 quizzes
 * passed. A pure server-side gate therefore emits nothing on precisely the
 * screen the certificate CTA is for, and only shows the button on a later
 * revisit.
 *
 * So `show_quiz_result_box.php` emits the button pre-hidden, carrying
 * `data-bnrmp-cert-on-pass`, when it has determined server-side that every
 * OTHER step of the course is already complete — i.e. passing this quiz
 * completes it. This function does nothing but drop the `hidden` attribute.
 *
 * 🔴 THE PASS/FAIL DECISION IS NOT MADE HERE. The trigger is LearnDash's own
 * public jQuery event `learndash-quiz-finished-passed` (wpProQuiz_front.js:2641),
 * which LD fires ONLY on pass, from the same branch that adds
 * `.ld-quiz-result-passed`. Nothing in this file inspects a score, compares a
 * threshold, or reads a percentage — it reacts to LearnDash's verdict. On FAIL
 * the event never fires and the button stays hidden.
 *
 * `.ld-quiz-result-passed` is also checked as a fallback for a render where the
 * event already fired before this ran (a cached result render), which is the
 * same defensive shape `relocate()` above uses.
 *
 * FAILURE MODE: if this never runs, the button stays hidden and the learner
 * reaches the certificate the way they already could — the dashboard link and
 * the cert-ready notice, both unaffected. Degraded, never misleading: a hidden
 * button cannot promise a certificate that was not earned.
 *
 * @param {HTMLElement} row The `.bnrmp-result__actions` row.
 * @return {boolean} True once the pending button has been revealed.
 */
function revealCertificateCta( row ) {
	const pending = row.querySelector( '.bnrmp-result__action--certificate[data-bnrmp-cert-on-pass]' );

	if ( ! pending ) {
		return false;
	}

	if ( ! document.querySelector( '.wpProQuiz_content.ld-quiz-result-passed' ) ) {
		return false;
	}

	pending.removeAttribute( 'hidden' );
	return true;
}

export function initQuizResultActions() {
	const row = document.querySelector( '.bnrmp-result__actions' );

	if ( ! row ) {
		return;
	}

	/*
	 * LearnDash fires this on its own jQuery instance. Bind through jQuery when
	 * it is present — a jQuery-triggered event does NOT reach a plain
	 * addEventListener listener, so a native binding would silently never fire.
	 * The MutationObserver below is the belt-and-braces path when jQuery is
	 * absent, since it already watches the same content for the button move.
	 */
	if ( window.jQuery ) {
		window.jQuery( document ).on( 'learndash-quiz-finished-passed', () => {
			revealCertificateCta( row );
		} );
	}

	revealCertificateCta( row );

	const content = document.querySelector( '.wpProQuiz_content' );

	if ( ! content ) {
		return;
	}

	// The controls may already be present (a cached result render).
	relocate( row );

	// LearnDash reveals the result box by toggling styles and injecting the
	// action buttons after the scoring AJAX settles, so watch until they appear.
	// The observer disconnects once both controls have been placed, so it does
	// not idle for the life of the page.
	const observer = new MutationObserver( () => {
		relocate( row );

		// The result STATE class lands on `.wpProQuiz_content` in the same pass
		// that reveals the box, so the observer sees it. This is the no-jQuery
		// path for the certificate CTA; when jQuery is present the event above
		// has usually already done it and this is a no-op.
		revealCertificateCta( row );

		const remaining = document.querySelector(
			'.ld-quiz-actions input.wpProQuiz_button_restartQuiz, .ld-quiz-actions input.wpProQuiz_button_reShowQuestion'
		);

		/*
		 * Keep observing while a certificate button is still pending AND no
		 * result state has been decided yet — the state class can land after
		 * the buttons have been moved.
		 *
		 * The `resolved` test is what stops this observing forever on a FAIL:
		 * once EITHER state class exists the verdict is final for this attempt,
		 * so a still-hidden button means "failed", not "not yet". Without it the
		 * observer would idle for the life of the page on every failed attempt.
		 */
		const certPending = row.querySelector(
			'.bnrmp-result__action--certificate[data-bnrmp-cert-on-pass][hidden]'
		);
		const resolved = document.querySelector(
			'.wpProQuiz_content.ld-quiz-result-passed, .wpProQuiz_content.ld-quiz-result-failed'
		);

		if ( ! remaining && ( ! certPending || resolved ) ) {
			observer.disconnect();
		}
	} );

	observer.observe( content, { childList: true, subtree: true } );
}
